import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { Router, RouterOutlet, RouterStateSnapshot, RoutesRecognized } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Permission, userHasSuperAdminRole } from '@phoenix/common';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { ToastModule } from 'primeng/toast';
import { distinctUntilChanged, filter } from 'rxjs';
import { OverlayHostComponent } from './@core/components/overlay-host/overlay-host.component';
import { CssLoader } from './@core/services/css-loader.service';
import { I18nService } from './@core/services/i18n/i18n.service';
import { ModalService } from './@core/services/modal/modal.service';
import { NotificationService } from './@core/services/notification/notification.service';
import { PerformanceLoggerService } from './@core/services/performance-logger.service';
import { SystemService } from './@core/services/system.service';
import { AnalyticsService } from './@core/utils/analytics.service';
import { reloadPageNoCache } from './@core/utils/reload-page-no-cache';
import { AppConfigWidgetComponent } from './layout/config/app-config-widget/app-config-widget.component';
import { RestoreConfirmationService } from './modules/admin/services/restore-confirmation.service';
import { BackupTriggerProgressService } from './modules/admin/services/backup-trigger-progress.service';
import { HotkeysCheatsheetComponent } from './modules/hotkeys/components/hotkeys-cheatsheet/hotkeys-cheatsheet.component';
import { ShortcutContextMenuHostComponent } from './modules/hotkeys/components/shortcut-context-menu-host/shortcut-context-menu-host.component';
import { AppInfoService } from './shared/services/app-info.service';
import { AuthService } from './shared/services/auth.service';
import { UserPermissionService } from './shared/services/user-permission.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    NgxUiLoaderModule,
    RouterOutlet,
    OverlayHostComponent,
    ToastModule,
    AppConfigWidgetComponent,
    HotkeysCheatsheetComponent,
    ShortcutContextMenuHostComponent
  ]
})
export class AppComponent implements OnInit {

  private i18nService = inject(I18nService);

  public menuMode: any;
  protected destroyRef = inject(DestroyRef);
  private modalService = inject(ModalService);
  private swUpdate = inject(SwUpdate);
  private httpClient = inject(HttpClient);
  private cssLoader = inject(CssLoader);
  // private location = inject(Location);
  private analytics = inject(AnalyticsService);
  private appInfoService = inject(AppInfoService);
  private systemService = inject(SystemService);
  // private permissionPipe = inject(HasPermissionPipe);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private performanceLogger = inject(PerformanceLoggerService);
  private restoreConfirmationService = inject(RestoreConfirmationService);
  private backupTriggerProgressService = inject(BackupTriggerProgressService);
  private userPermissionService = inject(UserPermissionService);
  private router = inject(Router);
  private titleService = inject(Title);

  constructor() {
    this.destroyRef.onDestroy(() => {
      console.log(`Destroying ${this.constructor.name}`);
    });

    afterNextRender({
      read: () => {
        this.systemService.globalMessageSubscription$().subscribe(x => {
          let globalMsg = x.globalMessageEvent.msg;
          const msg = this.i18nService.translate(_('common.global-message-around'));
          let sub = this.modalService.showConfirmationDialogWithCustomButtons(msg + new Date().toLocaleString(), globalMsg, [
            {
              label: this.i18nService.translate(_('common.confirm')),
              type: 'primary',
              returnValue: true,
              isFocus: true,
              dataTestId: 'yesButtonDialog'
            },
            {
              label: this.i18nService.translate(_('common.cancel')),
              type: 'secondary',
              returnValue: false,
              dataTestId: 'noButtonDialog'
            },
          ], undefined, undefined, 'globalMessageDialog').subscribe(result => {
            if (result) {
              console.log('Confirmed');
              sub.unsubscribe();
            }
            else {
              console.log('Cancelled');
              sub.unsubscribe();
            }
          });
        });
      }
    },);
  }

  async ngOnInit() {
    this.performanceLogger.startTiming('App Initialization');

    this.appInfoService.resetActiveViewNameToDefaultAppTitle();
    this.router.events.pipe(
      filter((event): event is RoutesRecognized => event instanceof RoutesRecognized),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((event) => this.applyRoutePageTitle(event.state));
    this.applyRoutePageTitle(this.router.routerState.snapshot);

    this.analytics.trackPageViews();

    // Initialize performance monitoring
    this.performanceLogger.initWebVitals();

    // DEV-2898: Deferred pending-restore check for ManageBackup only (no global poll for all sessions).
    this.authService.userDetails$
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((user) => {
        if (!user) {
          this.restoreConfirmationService.onAuthSessionEnded();
          this.backupTriggerProgressService.onAuthSessionEnded();
          return;
        }
        this.scheduleDeferredRestoreConfirmationCheckForManageBackup();
      });

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe({
        next: (event: VersionEvent) => {
          if (event?.type === 'VERSION_DETECTED') {
            let msg = this.i18nService.translate(_('common.version-updated'));
            alert(msg);
            this.i18nService.reloadLanguageCache(); //this doesnt do anything, this would need to be done after the reload

            const currentUser = this.authService.getCurrentUser();

            if (userHasSuperAdminRole(currentUser)) {
              this.notificationService.info(_('common.app-updated-admin-only'));
            } else
              reloadPageNoCache(this.httpClient);

          } else if (event?.type === 'NO_NEW_VERSION_DETECTED') {
            // console.log('No new version detected', event);
          } else if (event?.type === 'VERSION_READY') {
            console.log(`Current app version: ${event.currentVersion.hash}`);
            console.log(`New app version ready for use: ${event.latestVersion.hash}`);
          } else if (event?.type === 'VERSION_INSTALLATION_FAILED') {
            console.log(`Failed to install app version '${event.version.hash}': ${event.error}`);
          }
        },
        error: (err) => {
          console.error("Error in versionUpdates", err);
        },
        complete: () => {
          console.log("Version Updates Completed");
        }
      });
    }

    this.performanceLogger.endTiming('App Initialization');
  }

  /**
   * Deepest route `data.pageTitle` (i18n key) becomes the browser title.
   * Applied when the route is recognized, so a page can still replace it
   * with a context-specific title after it loads.
   */
  private applyRoutePageTitle(state: RouterStateSnapshot): void {
    let route = state.root;
    let titleKey: string | undefined;
    while (route) {
      const key = route.data['pageTitle'];
      if (typeof key === 'string' && key.length > 0) {
        titleKey = key;
      }
      if (!route.firstChild) {
        break;
      }
      route = route.firstChild;
    }
    if (!titleKey) {
      return;
    }
    const translated = this.i18nService.translate(titleKey);
    this.appInfoService.ActiveViewName.next(translated);
    this.titleService.setTitle(translated);
  }

  /**
   * After login (or cached session), defer work off the critical path; only users with ManageBackup query pending restore confirmation.
   */
  private scheduleDeferredRestoreConfirmationCheckForManageBackup(): void {
    const run = async (): Promise<void> => {
      try {
        const hasManageBackup = await this.userPermissionService.hasPermissionAsync(Permission.ManageBackup);
        if (hasManageBackup) {
          await this.restoreConfirmationService.checkAndShowRestoreConfirmation();
        } else {
          this.restoreConfirmationService.stopPendingConfirmationPolling();
        }
      } catch {
        /* non-fatal */
      }
    };

    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => void run());
    } else {
      setTimeout(() => void run(), 0);
    }
  }

  onToastClose(event: any): void {
    // Call the onClose callback if it exists in the message data
    if (event?.message?.data?.onClose) {
      event.message.data.onClose();
    }
  }

}
